/** Квант (штук в коробе) «Подбора поставки» и галочка «Округлять до кванта». Квант — из 1С тем же
 *  мостом, что у «Распределения» (`GET /in-box`: спека канала WB, SPEC_IN_BOX; ключ — nm). Квант
 *  живёт на номенклатуре 1С, поэтому у всех размеров nm он один. Округление — ВВЕРХ до кратного
 *  кванту по каждому региону (как `fbs_calc.round_up_to_box`); квант не задан / конфликт / не
 *  загружен — не округляем и помечаем (как флаг `no_in_box`), а не подставляем 1. Галочка —
 *  личная, в localStorage (`{v: 1, on}`); не прочиталась — выключена и текст на экране. */
import type { WbInBox, WbOffer } from "@/api/wbRegions";
import { browserStorage, type StorageLike } from "./supplyDraft";

export const ROUND_STORAGE_KEY = "seller-helper:wb-regions:supply-round-box:v1";
const ROUND_VERSION = 1;

export const ROUND_HINT =
  "Округление ВВЕРХ до целого числа коробов по каждому региону: «С квантом» = ⌈Нужно / квант⌉ × квант " +
  "(как в «Распределении»). Квант — штук в коробе из спеки канала WB в 1С. Квант не задан или у nm несколько " +
  "разных квантов (конфликт) — не округляется, помечено. С галочкой в Excel «Количество» = «С квантом», без неё — «Нужно».";

export type Quant =
  /** `partial` — у части карточек 1С этого nm квант не задан (взят заданный) */
  | { kind: "ok"; inBox: number; partial: boolean }
  /** nm есть в 1С, квант в спеке не задан */
  | { kind: "unset" }
  /** nm нет в 1С (или у оффера нет nm) — сопоставить не с чем */
  | { kind: "no-nm" }
  /** у номенклатур одного nm разные кванты — не выбираем */
  | { kind: "conflict"; values: readonly number[] }
  /** ответ 1С ещё не пришёл или с ошибкой */
  | { kind: "unloaded" };

export type QuantIndex = ReadonlyMap<string, Quant>;

/** Ответ `/in-box` → nm → квант. */
export function quantIndex(data: WbInBox): QuantIndex {
  const out = new Map<string, Quant>();
  for (const it of data.items) {
    if (it.conflict.length > 1) out.set(it.nm_id, { kind: "conflict", values: it.conflict });
    else if (it.in_box !== null) out.set(it.nm_id, { kind: "ok", inBox: it.in_box, partial: it.partial });
    else out.set(it.nm_id, { kind: "unset" });
  }
  return out;
}

/** Квант оффера по nm; индекса нет (1С не ответила) — `unloaded`. */
export function quantOf(o: WbOffer, index: QuantIndex | null): Quant {
  if (index === null) return { kind: "unloaded" };
  if (o.mp_sku === null) return { kind: "no-nm" };
  return index.get(String(o.mp_sku)) ?? { kind: "no-nm" };
}

/** Итог региона: с галочкой и известным квантом — вверх до кратного, иначе «Нужно» как есть. */
export function roundQty(need: number, q: Quant, round: boolean): number {
  if (!round || q.kind !== "ok" || need <= 0) return need;
  return Math.ceil(need / q.inBox) * q.inBox;
}

/** Текст пометки кванта (для подсказки и ячейки); у известного — null. */
export function quantProblem(q: Quant): string | null {
  switch (q.kind) {
    case "ok":
      return null;
    case "unset":
      return "квант не задан в спеке WB в 1С — не округляется";
    case "no-nm":
      return "nm нет в 1С (реквизит nm на номенклатуре) — квант не задан, не округляется";
    case "conflict":
      return `конфликт: у номенклатур этого nm в 1С разные кванты (${q.values.join(", ")}) — не округляется`;
    case "unloaded":
      return "квант из 1С не загружен — не округляется";
  }
}

/** Предупреждение у известного кванта: округляем, но квант задан не у всех карточек nm. */
export function quantPartialNote(q: Quant): string | null {
  return q.kind === "ok" && q.partial ? "квант задан не у всех карточек 1С этого nm — округляется по заданному" : null;
}

/** Причина пустого кванта для колонки «Квант» в xlsx (`in_box_note`); у известного — null. */
export function quantExportNote(q: Quant): string | null {
  switch (q.kind) {
    case "ok":
      return null;
    case "unset":
      return "не задан";
    case "no-nm":
      return "нет nm в 1С";
    case "conflict":
      return `конфликт: ${q.values.join(", ")}`;
    case "unloaded":
      return "не загружен";
  }
}

export interface RoundLoad {
  on: boolean;
  error: string | null;
}

export function loadRound(getStorage: () => StorageLike = browserStorage): RoundLoad {
  let raw: string | null;
  try {
    raw = getStorage().getItem(ROUND_STORAGE_KEY);
  } catch (e) {
    return { on: false, error: `настройка «Округлять до кванта» не прочитана из браузера (${String(e)}) — выключена` };
  }
  if (raw === null) return { on: false, error: null };
  try {
    const p = JSON.parse(raw) as { v?: unknown; on?: unknown } | null;
    if (typeof p === "object" && p !== null && p.v === ROUND_VERSION && typeof p.on === "boolean") return { on: p.on, error: null };
  } catch {
    // не JSON — та же ветка, что чужой формат
  }
  return { on: false, error: "настройка «Округлять до кванта» в браузере повреждена — выключена" };
}

export function saveRound(on: boolean, getStorage: () => StorageLike = browserStorage): string | null {
  try {
    getStorage().setItem(ROUND_STORAGE_KEY, JSON.stringify({ v: ROUND_VERSION, on }));
    return null;
  } catch (e) {
    return `настройка «Округлять до кванта» не сохраняется в браузере (${String(e)}) — сбросится при закрытии вкладки`;
  }
}
